<template>
  <ion-page>
    <ion-content>
      <the-header :title="t('quickCalmingTools')" />
      <div class="quick-calming content-container">
        <therapy-list-tabs
          class="quick-calming__tabs"
          :tabs="listTabs"
          :selected-tab-value="state.selectedListTabValue"
          @update:selected-tab-value="state.selectedListTabValue = $event"
        />
        <div class="quick-calming__list">
          <therapy-video-item
            class="quick-calming__list-item"
            v-for="item in quickCalmBarItemsFiltered"
            :key="item.id"
            :item="item"
            square
            title-on-img
            hide-tag
            @item:click="handleQuickCalmClick"
          />
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts" setup>
import { computed, reactive } from 'vue';
import { useRouter } from 'vue-router';

import {
  formatStringForUrlQuery,
  getFirebaseStorageFileUrl,
} from '@/services/methods';
import { TherapyBarItem, UiTherapyTabItemI } from '@/types/ui';
import api from '@/api';
import { onIonViewWillEnter, IonPage, IonContent } from '@ionic/vue';
import { useInternalization } from '@/composables/useInternalization';

import TherapyVideoItem from '@/components/TherapyVideoItem.vue';
import TheHeader from '@/components/TheHeader.vue';
import TherapyListTabs from '@/components/TherapyListTabs.vue';

const router = useRouter();
const { t } = useInternalization();

const listTabs = computed<UiTherapyTabItemI[]>(() => [
  { value: 'all', title: t('quickCalmingListTabs.all') },
  { value: 'stress', title: t('quickCalmingListTabs.stress') },
  { value: 'panic', title: t('quickCalmingListTabs.panic') },
  { value: 'anxiety', title: t('quickCalmingListTabs.anxiety') },
  { value: 'fear', title: t('quickCalmingListTabs.fear') },
  { value: 'anger', title: t('quickCalmingListTabs.anger') },
  { value: 'pain', title: t('quickCalmingListTabs.pain') },
  { value: 'conflict', title: t('quickCalmingListTabs.conflict') },
  { value: 'tension', title: t('quickCalmingListTabs.tension') },
  { value: 'fallAsleep', title: t('quickCalmingListTabs.fallAsleep') },
  { value: 'panickAtacks', title: t('quickCalmingListTabs.panickAtacks') },
  {
    value: 'negativeThought',
    title: t('quickCalmingListTabs.negativeThought'),
  },
]);

const state = reactive({
  quickCalmItems: [] as TherapyBarItem[],
  selectedListTabValue: listTabs.value[0].value as UiTherapyTabItemI['value'],
});

const quickCalmBarItemsFiltered = computed(() => {
  if (state.selectedListTabValue === 'all') {
    return state.quickCalmItems;
  }
  return state.quickCalmItems.filter(
    (item) =>
      item.type?.toLowerCase() === state.selectedListTabValue.toLowerCase()
  );
});

onIonViewWillEnter(async () => {
  await getQuickCalmVideos();
});

async function getQuickCalmVideos() {
  console.log('GETTING QUICK CALM VIDEOS');
  const res = await api.courses.getAllQuickCalmVideos();
  console.log('state.quickCalmVideos', res);

  state.quickCalmItems = res.map((v) => ({
    id: v.id,
    title: v.title,
    text: v.text,
    img: getFirebaseStorageFileUrl(v.imgPath),
    type: v.type,
    translations: v.translations,
  }));
}

function handleQuickCalmClick(item: TherapyBarItem) {
  router.push({
    name: 'TherapyQuickCalmVideo',
    params: { id: item.id },
    query: { title: formatStringForUrlQuery(item.title) },
  });
}
</script>

<style lang="scss" scoped>
.quick-calming {
  margin-bottom: 4em;

  &__list {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 0 auto;
    gap: 10px;
    margin-bottom: 7em;
    max-width: 400px;

    @media (min-width: 720px) {
      grid-template-columns: 1fr 1fr 1fr;
      max-width: 600px;
    }

    @media (min-width: 1020px) {
      grid-template-columns: 1fr 1fr 1fr 1fr;
      max-width: 800px;
    }

    &-item {
      :deep(img) {
        border-radius: 20px;
        overflow: hidden;
        object-fit: cover !important;
      }
    }
  }
}
</style>
